/**
 * Display helpers for lease-return snaps (mirrors api/ai/lease-returns/sections.mjs).
 * Applied in the UI so an already-saved report improves without a re-run.
 */

export const SECTION_GROUP_LABELS = [
  ["economics", "Economics"],
  ["reserves_eol", "Reserves & EOL compensation"],
  ["airframe", "Maintenance — airframe"],
  ["engines", "Maintenance — engines"],
  ["components", "Maintenance — LG / APU / props"],
  ["delivery", "Delivery / redelivery"],
  ["records", "Records & appearance"],
  ["special", "Credits, exceptions & special cases"],
  ["other", "Other contract conditions"],
];

const GROUP_ALIASES = {
  overview: "delivery",
  ops: "delivery",
  airworthiness: "delivery",
  checks: "delivery",
  commercial: "economics",
};

const EXTRA_ALIASES = {
  base_rent: "rent",
  basic_rent: "rent",
  monthly_rent: "rent",
  rates_engines: "rates_engines_prsv",
  rates_engine_prsv: "rates_engines_prsv",
  rates_engine_llp: "rates_engines_llp",
  supplemental_rent: "reserve_modality",
  maintenance_payments: "reserve_modality",
  airframe_llp: "engines_llp",
  ad_compliance: "ad_clearance",
  cofa_export: "standard",
  paint_livery: "paint",
  structural_repairs: "repairs",
  final_inspection_demo_flight: "demo_flight",
  late_return_holdover: "holdover_rent",
  nonconformity_threshold: "eol_adjustments",
  return_check: "eol_delivery_check",
  redelivery_check: "eol_delivery_check",
  delivery_check: "eol_delivery_check",
  c_check_compensation: "eol_delivery_check",
  return_condition_compensation: "eol_type",
  redelivery_maintenance_compensation: "eol_type",
  maintenance_adjustment: "eol_type",
  cut_off_date: "delivery_cutoff",
  delivery_cut_off: "delivery_cutoff",
  foc_sb: "foc_sb_kit",
  totalcare: "engine_oem_program",
  pasv: "engine_oem_program",
  epr_option: "lessor_termination_option",
  rent_premium: "rent_premium_acceleration",
  smiles_notes: "settled_payables",
  glide_payables: "settled_payables",
  lessor_cap: "tripartite_cost_split",
  ber_engine: "ber_replacement",
  engine_sale: "engine_sale_carveout",
  collateral_balance: "maintenance_collateral",
};

export const STD_KEY_ORDER = [
  "rent",
  "security_deposit",
  "chapter11",
  "contribution_rent",
  "rent_premium_acceleration",
  "settled_payables",
  "walkaway_date",
  "delivery_cutoff",
  "holdover_rent",
  "covid_rent_deferral",
  "reserve_modality",
  "eol_type",
  "eol_credit",
  "maintenance_collateral",
  "credit_test",
  "rates_engines_prsv",
  "rates_engines_llp",
  "rates_lg",
  "rates_apu",
  "rates_propellers",
  "rates_airframe",
  "escalation_engines",
  "escalation_airframe",
  "eol_adjustments",
  "ad_cost_sharing",
  "mpd_clearance",
  "hard_times",
  "occm",
  "rotable_ageing",
  "engines_general",
  "engines_llp",
  "engines_bsi",
  "engines_ecm",
  "engine_oem_program",
  "tripartite_cost_split",
  "engine_reinstallation",
  "landing_gear",
  "apu",
  "propellers",
  "tires_brakes",
  "general",
  "location",
  "eol_delivery_check",
  "ferry_flight",
  "demo_flight",
  "registration_deregistration",
  "delivery_schedule",
  "redelivery_schedule",
  "fuel_at_return",
  "standard",
  "modifications_stc",
  "repairs",
  "ad_clearance",
  "software",
  "foc_sb_kit",
  "storage_redelivery",
  "non_material_caps",
  "companion_cross_conditioning",
  "lessor_termination_option",
  "ber_replacement",
  "engine_sale_carveout",
  "records",
  "non_incident_statement",
  "paint",
  "placards_language",
  "cabin_softs",
  "related_swap",
  "pre_delivery_mods",
];

const STD_LABELS = {
  rent: "Rent / basic rent",
  security_deposit: "Security deposit",
  chapter11: "Chapter 11 / insolvency",
  contribution_rent: "Contribution Rent / lessor engine contribution",
  walkaway_date: "Walkaway (map Cut-Off / options — rare literal)",
  delivery_cutoff: "Delivery Cut-Off / substitution rights",
  holdover_rent: "Holdover rent",
  covid_rent_deferral: "Insolvency / COVID rent deferral amendments",
  reserve_modality: "Maintenance reserves (cash / LC / held)",
  eol_type: "EOL true-up / compensation",
  eol_credit: "EOL Credit / Return Compensation Credit",
  rates_engines: "Rates — engine PRSV",
  rates_engines_prsv: "Rates — engine PRSV",
  rates_engines_llp: "Rates — engine LLP",
  rates_lg: "Rates — landing gear",
  rates_apu: "Rates — APU",
  rates_propellers: "Rates — propellers",
  rates_airframe: "Rates — airframe checks",
  escalation_engines: "Escalation — engines",
  escalation_airframe: "Escalation — airframe / LG / APU / props",
  eol_adjustments: "EOL adjustments / nonconformity",
  ad_cost_sharing: "AD cost-sharing",
  engines_llp: "Engines — LLPs (condition)",
  engines_bsi: "Engines — BSI",
  engine_oem_program: "Engine OEM program (TotalCare / PASV / tripartite)",
  location: "Location / place / check",
  eol_delivery_check: "Return check / C-check / cash-in-lieu",
  demo_flight: "Demo / final inspection / BSI flight",
  registration_deregistration: "Deregistration / export CoA",
  software: "Software / LSAP",
  foc_sb_kit: "FOC SB kit title transfer",
  storage_redelivery: "Storage upon redelivery",
  non_material_caps: "Non-material discrepancy acceptance caps",
  companion_cross_conditioning: "Companion / portfolio cross-conditioning",
  paint: "Paint (extent, where)",
  related_swap: "Engine / APU / LG swap & BER replacement",
};

function parseLooseAmount(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) return Number(s.replace(/,/g, ""));
  if (/^\d{4,},\d{3}(\.\d+)?$/.test(s)) return Number(s.replace(/,/g, ""));
  if (/^\d+\.\d{3},\d{2}$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatAmount(n) {
  if (!Number.isFinite(n)) return null;
  const ints = Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-9;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: ints ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function formatMoneyInText(text) {
  let t = String(text || "");
  // House date format: 2015-04-16 → 16-Apr-2015.
  t = t.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, y, m, d) => {
    const mi = Number(m) - 1;
    if (mi < 0 || mi > 11) return `${y}-${m}-${d}`;
    return `${d}-${MONTHS_ABBR[mi]}-${y}`;
  });
  t = t.replace(/\$\s*([\d.,]+)/g, (_, raw) => {
    const n = parseLooseAmount(raw);
    return n == null ? `USD ${raw}` : `USD ${formatAmount(n)}`;
  });
  t = t.replace(/\b(USD|EUR|GBP|AED|CHF)\s*([\d.,]+)/gi, (_, cur, raw) => {
    const n = parseLooseAmount(raw);
    return n == null ? `${cur.toUpperCase()} ${raw}` : `${cur.toUpperCase()} ${formatAmount(n)}`;
  });
  t = t.replace(
    /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{4})\s*\$/gi,
    "$1 $2 USD"
  );
  return t.replace(/[ \t]+/g, " ").trim();
}

/** Capitalize the first letter and close the line with a period. */
export function sentenceLine(line) {
  let l = String(line || "").trim();
  if (!l) return "";
  const i = l.search(/[a-zA-Z]/);
  // Don't capitalize mid-token letters like the "th" in "1/20th".
  if (i >= 0 && (i === 0 || !/[0-9a-zA-Z]/.test(l[i - 1]))) {
    l = l.slice(0, i) + l[i].toUpperCase() + l.slice(i + 1);
  }
  if (/[a-zA-Z0-9%)\u2019"']$/.test(l) && !/[.:;!?…]$/.test(l)) l += ".";
  return l;
}

/** Split a line into bullet items on " – " and ";" — but never inside parentheses. */
function splitBulletItems(line) {
  const parts = [];
  let depth = 0;
  let cur = "";
  const s = String(line);
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (depth === 0) {
      if (ch === ";") {
        if (cur.trim()) parts.push(cur.trim());
        cur = "";
        continue;
      }
      if ((ch === "–" || ch === "•" || ch === "▪") && /\s$/.test(cur) && /^\s/.test(s.slice(i + 1) || " ")) {
        if (cur.trim()) parts.push(cur.trim());
        cur = "";
        continue;
      }
    }
    cur += ch;
  }
  if (cur.trim()) parts.push(cur.trim());
  return parts.length ? parts : [String(line).trim()];
}

export function snapToBullets(snap) {
  const lines = String(snap || "")
    .split(/\n+/)
    .flatMap((l) => splitBulletItems(l))
    .map((l) => l.replace(/^[-–•]\s*/, "").trim())
    .filter(Boolean)
    .map((l) => sentenceLine(formatMoneyInText(l)))
    .filter(Boolean);
  if (!lines.length) return "";
  return lines.map((l) => `– ${l}`).join("\n");
}

/** Keys allowed to keep money in the snap (reserves / economics). */
const MONEY_OK_KEYS = new Set([
  "rent",
  "security_deposit",
  "holdover_rent",
  "walkaway_date",
  "delivery_cutoff",
  "chapter11",
  "contribution_rent",
  "covid_rent_deferral",
  "reserve_modality",
  "eol_type",
  "eol_credit",
  "rates_engines",
  "rates_engines_prsv",
  "rates_engines_llp",
  "rates_lg",
  "rates_apu",
  "rates_propellers",
  "rates_airframe",
  "escalation_engines",
  "escalation_airframe",
  "eol_adjustments",
  "ad_cost_sharing",
  "non_material_caps",
]);

const MONEY_LINE_RE =
  /\b(USD|EUR|GBP|AED|CHF)\b|\b\$\s*\d|\/\s*(FH|FC|MO|HR)\b.*\d|\d[\d,.]*\s*(USD|EUR)\b|\blist\s*price\b|\bCLP\b|\bper\s+(FC|FH)\s*=/i;
const RATE_JARGON_RE =
  /\b(assumed ratio|invoiced on|true-?up|derate|esc\.|adjustment date|economics|fh\s*:\s*fc|per engine|per aircraft|ebsv|catalogue|shop visit rate|maintenance payment)\b/i;
const WHO_PAYS_LINE_RE =
  /\b(lessor|lessee)\s+(cost|expense|pays|payment)\b|\bat\s+(lessor|lessee)\s+(cost|expense)\b|\bwho\s+pays\b|\b(lessor|lessee)\s+expense\b/i;
const RESERVE_FORMULA_RE =
  /^\s*[-–•]?\s*reserve\b|\blist\s*price\s*[÷\/]|÷\s*95%|notional\s+account|maintenance\s+payment/i;

/**
 * Display-time hygiene so an already-saved report drops rates / who-pays from
 * condition rows without a re-run. Returns { snap, noteExtra }.
 */
export function hygienizeSnap(key, snap) {
  const k = String(key || "").trim().toLowerCase();
  if (!k || MONEY_OK_KEYS.has(k)) return { snap: String(snap || ""), noteExtra: "" };
  const lines = String(snap || "")
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const keep = [];
  const whoPays = [];
  for (const line of lines) {
    if (WHO_PAYS_LINE_RE.test(line)) {
      whoPays.push(line.replace(/^[-–•]\s*/, "").trim());
      continue;
    }
    if (MONEY_LINE_RE.test(line) || RATE_JARGON_RE.test(line) || (k === "engines_llp" && RESERVE_FORMULA_RE.test(line))) {
      continue; // rates live on rates_* rows
    }
    keep.push(line);
  }
  return {
    snap: keep.join("\n"),
    noteExtra: whoPays.join(" "),
  };
}

export function canonicalGroup(group) {
  const g = String(group || "other");
  return GROUP_ALIASES[g] || g;
}

export function foldRows(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row?.key) continue;
    const key = String(row.key)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const mapped = EXTRA_ALIASES[key] || key;
    const incoming = { ...row, key: mapped, group: canonicalGroup(row.group) };
    if (STD_LABELS[mapped]) incoming.label = STD_LABELS[mapped];
    const prev = byKey.get(mapped);
    if (!prev) {
      byKey.set(mapped, incoming);
      continue;
    }
    if (incoming.present && !prev.present) byKey.set(mapped, { ...prev, ...incoming, key: mapped });
    else if (
      incoming.present &&
      prev.present &&
      String(incoming.snap || "").length > String(prev.snap || "").length
    ) {
      byKey.set(mapped, { ...prev, ...incoming, key: mapped });
    }
  }
  return [...byKey.values()];
}

export function injectCommercialRows(rows, commercial = {}, header = {}) {
  const byKey = new Map(foldRows(rows).map((r) => [r.key, r]));
  const ensure = (key, label, group, block, fallback) => {
    const snap =
      snapToBullets(block?.snap || block?.summary || fallback || "").replace(/^–\s*/, "") || null;
    const prev = byKey.get(key);
    if (prev?.present) return;
    if (!snap || snap === "—") return;
    byKey.set(key, {
      key,
      label,
      group,
      present: true,
      snap: block?.snap || fallback,
      source: prev?.source || block?.source || null,
      note: prev?.note || block?.note || null,
    });
  };
  ensure("rent", STD_LABELS.rent, "economics", commercial.rent, header.rentSnap);
  ensure("security_deposit", STD_LABELS.security_deposit, "economics", commercial.securityDeposit, header.securityDepositSnap);
  ensure("chapter11", STD_LABELS.chapter11, "economics", commercial.chapter11, header.chapter11Snap);
  ensure(
    "reserve_modality",
    STD_LABELS.reserve_modality,
    "reserves_eol",
    commercial.reservePaymentType,
    header.reserveSnap
  );
  return [...byKey.values()];
}

export function shortIdent(val, fallback) {
  const v = String(val || "").trim();
  const fb = fallback ? String(fallback).trim() : "";
  if (!v) return fb || "—";
  if (v.length > 48 || /\bper p\.|\bindicating\b|\bbut the\b|\bnot directly\b/i.test(v)) {
    return fb || v.split(/[;(]/)[0].trim() || "—";
  }
  return v;
}

const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** dd-Mmm-yyyy plus time (e.g. 12-Aug-2026, 2:33 PM) — house date format. */
export function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const date = `${String(d.getDate()).padStart(2, "0")}-${MONTHS_ABBR[d.getMonth()]}-${d.getFullYear()}`;
  return `${date}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
